import React from "react";
import PropTypes from "prop-types";
import { Icon, Tag, message } from "antd";

/**
 * Company (entity-level) Initial Screening — a compact inline block that
 * lives INSIDE the first Company Detail card (right under identity), NOT its
 * own separate section. Renders the suggested action chip + Probable /
 * Confirmed hit summary + a Download Report link. Silently hidden when the
 * payload has nothing on record.
 */

const ACTION_TONE = {
    PROCEED: "green",
    REVIEW: "red",
    BLOCK: "red",
    HOLD: "orange"
};

const HitCell = ({ label, value }) => {
    const clean = !value || String(value).trim() === "";
    return (
        <div className={"cc-scr__cell"}>
            <div className={"cc-scr__label"}>{label}</div>
            <div
                className={
                    "cc-scr__value" +
                    (clean ? " cc-scr__value--clean" : " cc-scr__value--flag")
                }
            >
                {clean ? "No Hit" : value}
            </div>
        </div>
    );
};
HitCell.propTypes = { label: PropTypes.string, value: PropTypes.string };

const CompanyScreeningCard = ({ screening, companyName, docViewBlocked }) => {
    if (!screening) return null;
    const { action, probable, confirmed, reportUrl } = screening;
    const tone = action && ACTION_TONE[action];
    // Credit Appraisal team lacks doc-view permission — swap the Download
    // Report link for a stop-cursor button that shows a warning on click,
    // matching the KYC / verification-doc treatment.
    const blockedClick = e => {
        e.preventDefault();
        e.stopPropagation();
        message.warning(
            "You don't have permission to download this report.",
            3
        );
    };
    return (
        <div className={"cc-scr-inline"}>
            <div className={"cc-scr-inline__header"}>
                <span className={"cc-scr-inline__title"}>
                    Initial Screening
                </span>
                <span className={"cc-scr-inline__sub"}>
                    Entity AML / PEP / sanctions
                </span>
                <span className={"cc-scr-inline__spacer"} />
                <span className={"cc-scr__verdict-label"}>Suggested action</span>
                {action ? (
                    <Tag color={tone || "default"}>{action}</Tag>
                ) : (
                    <Tag>—</Tag>
                )}
                {reportUrl ? (
                    docViewBlocked ? (
                        <button
                            type={"button"}
                            className={"cc-scr__download"}
                            style={{ cursor: "not-allowed", opacity: 0.6, background: "none", border: 0 }}
                            onClick={blockedClick}
                            title={"You don't have permission to download this report"}
                        >
                            <Icon type={"stop"} /> Download Report
                        </button>
                    ) : (
                        <a
                            className={"cc-scr__download"}
                            href={reportUrl}
                            target={"_blank"}
                            rel={"noreferrer"}
                            download={`Initial_Screening_${companyName || "Entity"}.pdf`}
                        >
                            <Icon type={"download"} /> Download Report
                        </a>
                    )
                ) : null}
            </div>
            <div className={"cc-scr__grid"}>
                <HitCell label={"Probable Hit"} value={probable} />
                <HitCell label={"Confirmed Hit"} value={confirmed} />
            </div>
        </div>
    );
};

CompanyScreeningCard.propTypes = {
    // Parsed by parseCompanyScreening — pass the object, not the raw trackwizz.
    screening: PropTypes.shape({
        action: PropTypes.string,
        probable: PropTypes.string,
        confirmed: PropTypes.string,
        reportUrl: PropTypes.string,
        sourceSystemCustomerCode: PropTypes.string
    }),
    companyName: PropTypes.string,
    docViewBlocked: PropTypes.bool
};

export default CompanyScreeningCard;
